"use client";

// src/ui/components/calculator/protocol-modal.tsx
//
// Save / edit a protocol (handoff 02 SCREEN 3): bottom-sheet on mobile, centered
// dialog on desktop. Radix handles focus, Escape, scrim-close and exit presence;
// uncontrolled inputs reset on every fresh open. Keeps onSave(name,notes) unchanged.

import { useLayoutEffect, useRef } from "react";
import { X } from "lucide-react";
import { useTranslations } from "next-intl";
import { Button } from "@/ui/components/ui/button";
import { Input } from "@/ui/components/ui/input";
import { Label } from "@/ui/components/ui/label";
import { ToolDialog } from "./tool-dialog";

interface ProtocolModalProps {
	open: boolean;
	onClose: () => void;
	onSave: (name: string, notes: string) => void;
	initialName?: string;
	initialNotes?: string;
	isEdit?: boolean;
}

function ModalContent({
	open,
	onClose,
	onSave,
	initialName = "",
	initialNotes = "",
	isEdit = false,
}: ProtocolModalProps) {
	const t = useTranslations("peptideCalculator");
	const nameRef = useRef<HTMLInputElement>(null);
	const notesRef = useRef<HTMLTextAreaElement>(null);

	// The Radix presence phase retains the sheet briefly after close. Reset inputs
	// on each fresh open, including a reopen before the exit animation finishes.
	useLayoutEffect(() => {
		if (!open) return;
		if (nameRef.current) nameRef.current.value = initialName;
		if (notesRef.current) notesRef.current.value = initialNotes;
	}, [open, initialName, initialNotes]);

	const handleSave = () => {
		const name = nameRef.current?.value.trim() ?? "";
		const notes = notesRef.current?.value.trim() ?? "";
		if (!name || name.length > 100) return;
		onSave(name, notes);
	};

	const title = isEdit ? t("protocols.edit") : t("protocols.save");

	return (
		<ToolDialog
			open={open}
			onClose={onClose}
			title={title}
			className="sm:max-w-md"
			initialFocusRef={nameRef}
			onKeyDown={(event) => {
				if (event.key === "Enter" && event.ctrlKey) handleSave();
			}}
		>
			{/* Head */}
			<div className="flex items-center justify-between border-b border-border px-4 py-3 sm:px-5">
				<h2 className="text-base font-semibold text-foreground">{title}</h2>
				<button
					type="button"
					onClick={onClose}
					aria-label={t("protocols.close")}
					className="-mr-2 inline-flex h-11 w-11 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
				>
					<X className="h-5 w-5" />
				</button>
			</div>

			{/* Body */}
			<div className="space-y-4 overflow-y-auto p-4 sm:p-5">
				<div className="space-y-2">
					<Label htmlFor="protocol-name">{t("protocols.name")}</Label>
					<Input
						ref={nameRef}
						id="protocol-name"
						type="text"
						defaultValue={initialName}
						placeholder={t("protocols.namePlaceholder")}
						maxLength={100}
					/>
				</div>

				<div className="space-y-2">
					<Label htmlFor="protocol-notes">{t("protocols.notes")}</Label>
					<textarea
						ref={notesRef}
						id="protocol-notes"
						defaultValue={initialNotes}
						placeholder={t("protocols.notesPlaceholder")}
						maxLength={500}
						rows={3}
						className="flex w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
					/>
				</div>
			</div>

			{/* Actions */}
			<div className="flex justify-end gap-3 border-t border-border px-4 py-3 sm:px-5">
				<Button variant="outline-solid" onClick={onClose} className="min-h-11">
					{t("protocols.cancel")}
				</Button>
				<Button onClick={handleSave} className="min-h-11">
					{isEdit ? t("protocols.update") : t("protocols.saveButton")}
				</Button>
			</div>
		</ToolDialog>
	);
}

export function ProtocolModal(props: ProtocolModalProps) {
	return <ModalContent {...props} />;
}
